// Single source of truth for everything that varies between retreats.
//
// Before this file, each retreat's behavior was decided by comparing week.camp_name against a
// string literal in five different components, and the retreat total was written out longhand in
// seven places. Add a new retreat — or change a price — by editing RETREAT_CONFIG only.
//
// Keys must match the event table's camp_name exactly.

export const DEFAULT_RETREAT_CONFIG = {
   personalInfoForm: 'standard',   // which PersonalInfo variant the wizard renders
   hasRoomTypeChoice: false,       // show the standard/deluxe radio and its surcharge copy
   fixedRoomType: null,            // force a room_type without offering a choice
   roomSurcharge: 0,               // added to the total when deluxe is *chosen*
   deluxeRoomsFull: false,         // deluxe is sold out — show the radio disabled with a waitlist note
   craftFair: false,               // show the craft fair table checkbox
   chargesPerSon: false,           // each son listed is charged the event's full price
   fixedGender: null               // force a gender without offering a choice ('M' or 'F')
}

export const RETREAT_CONFIG = {
   // Couples Retreat uses deluxe rooms exclusively, so there is nothing to upgrade to. The room
   // type is still recorded so the rooming list is accurate, but it costs nothing and is never
   // mentioned to the registrant.
   'Couples Retreat': { personalInfoForm: 'couples', fixedRoomType: 'deluxe' },
   // "Men's Challenge" is the name in the event table. seed_retreat_events.sql says
   // "Men's Retreat" — the seed file is wrong and was never run as written.
   "Men's Challenge": { hasRoomTypeChoice: true, roomSurcharge: 50, fixedGender: 'M' },
   // Deluxe rooms are sold out for this retreat — the radio stays visible but disabled, with a
   // waitlist note in its place. Remove deluxeRoomsFull once deluxe inventory opens back up.
   "Ladies' Retreat": { hasRoomTypeChoice: true, roomSurcharge: 50, craftFair: true, fixedGender: 'F', deluxeRoomsFull: true },
   'Father and Son Adventure': { personalInfoForm: 'fatherSon', chargesPerSon: true, fixedGender: 'M' }
}

export const INITIAL_PROMO_CODE = {
   isSelected: false,
   promoSelected: '',
   promoType: null,
   discountPercent: 0,
   discountAmount: 0,
   promoId: null
}

export function getRetreatConfig(week) {
   return { ...DEFAULT_RETREAT_CONFIG, ...(RETREAT_CONFIG[week?.camp_name] ?? {}) }
}

// Blank rows are filtered out exactly the way the server does it before storing
// father_son_adventure_son_names, so the price always matches the names on the registration.
export function getSonCount(personalInformation) {
   return (personalInformation?.sonNames ?? [])
      .filter(name => (name ?? '').trim().length > 0)
      .length
}

// Returns 0 for retreats that never offered a choice, which is what keeps Couples Retreat free
// even though it stores room_type = 'deluxe'.
export function getRoomSurcharge(week, roomType) {
   const config = getRetreatConfig(week)
   return config.hasRoomTypeChoice && roomType === 'deluxe' ? config.roomSurcharge : 0
}

// A son attends the retreat in his own right, so he costs what his father costs: the event's
// registration fee plus its camp fee. Read off the event row rather than written down as a
// constant here, so changing the price in the event table changes the son fee with it.
export function getPerSonFee(week) {
   if (!getRetreatConfig(week).chargesPerSon) {
      return 0
   }
   return Number(week?.registration_fee ?? 0) + Number(week?.camp_fee ?? 0)
}

export function getSonSurcharge(week, sonCount) {
   return getPerSonFee(week) * (sonCount ?? 0)
}

// Everyone covered by this registration, the registrant himself included — a Father and Son record
// pays for the whole party. Every other flow registers one person, so it returns 1 and the shared
// payment screen behaves exactly as it did before.
export function getAttendeeCount(week, sonCount) {
   return getRetreatConfig(week).chargesPerSon ? 1 + (sonCount ?? 0) : 1
}

// What the "Registration Fee Only" option collects. The registration fee is charged per person, so
// a father bringing three sons owes four of them up front — not the one the event row lists.
export function getRegistrationFeeDue(week, sonCount) {
   return Number(week?.registration_fee ?? 0) * getAttendeeCount(week, sonCount)
}

// The camp fee plus everything that rides on top of it. Stored on the registration so the roster
// reconciles: registration.camp_fee + registration_fee equals the total charged.
export function getRetreatCampFee(week, { roomType, sonCount } = {}) {
   return Number(week?.camp_fee ?? 0)
      + getRoomSurcharge(week, roomType)
      + getSonSurcharge(week, sonCount)
}

export function getRetreatTotal(week, { roomType, sonCount } = {}) {
   return Number(week?.registration_fee ?? 0) + getRetreatCampFee(week, { roomType, sonCount })
}

// The Additional Details step has nothing to show unless one of its two sections applies. When it
// does not, the wizard wires around it rather than letting the step redirect itself — a step that
// navigates on mount makes the previous page's Previous button look broken.
export function showsAdditionalDetails(week) {
   const config = getRetreatConfig(week)
   return config.hasRoomTypeChoice || config.craftFair
}

// The room type a retreat starts with: forced where there is no choice, 'standard' where there is,
// and null where rooms are not part of the registration at all.
export function getDefaultRoomType(week) {
   const config = getRetreatConfig(week)
   if (config.fixedRoomType) {
      return config.fixedRoomType
   }
   return config.hasRoomTypeChoice ? 'standard' : null
}

// Percent and flat-amount promos were applied with the same three-line expression in five places.
export function applyPromo(amount, promoCode) {
   if (!promoCode?.isSelected) {
      return amount
   }
   if (promoCode.discountPercent > 0 && promoCode.discountPercent < 100) {
      return amount * (1 - promoCode.discountPercent / 100)
   }
   if (promoCode.discountAmount > 0) {
      return Math.max(0, amount - promoCode.discountAmount)
   }
   return amount
}
